import PropTypes from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { ButtonBase, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowDropDownSharpIcon from '@material-ui/icons/ArrowDropDownSharp';
import classNames from 'classnames';
import { useMeContext } from '~/modules/me';
import { getTodayForUser } from '~/modules/common/dates/today';
import { DelaStarIcon } from '~/modules/common/components/Icons';
import BusyIcon from '~/modules/resourcing/common/components/BusyIcon';
import { UserWithRoles } from '~/modules/common/components/User';
import { useGetResourceUser } from '~/modules/resourcing/common/hooks';
import {
  hasProposeActionAllowed,
  hasAcceptActionAllowed
} from '~/modules/resourcing/common/components/ResourceMatchItem/permissionUtil';
import { RESOURCE_MATCH_ITEM_BUTTONS } from '../enums';
import ResourceMatchItemSkeleton from './ResourceMatchItemSkeleton';
import { ResourceMatchItemAcceptButton } from './ResourceMatchItemAcceptButton';
import { ResourceMatchItemProposeButton } from './ResourceMatchItemProposeButton';
import useStyles from './useStyles';

const useMatchStyles = makeStyles(theme => ({
  matchInfo: {
    display: 'flex',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.spacing(0.75, 0, 0, 0.75),
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: theme.spacing(-0.25, 0, -0.25, -1),
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)'
    }
  },
  delaStarIcon: {
    padding: theme.spacing(0, 0, 0, 0.5)
  },
  matchButtonContainer: {
    display: 'block',
    width: '100%'
  }
}));

const useUserWithRolesStyles = makeStyles(theme => ({
  avatar: {
    width: theme.spacing(2.5),
    height: theme.spacing(2.5),
    fontSize: theme.spacing(1)
  },
  rows: {
    display: 'flex'
  },
  row: {
    width: 'inherit'
  },
  nameRow: {
    display: 'inline-block',
    textAlign: 'start',
    fontSize: theme.spacing(1.625),
    fontWeight: theme.typography.fontWeightRegular,
    maxWidth: theme.spacing(17.25)
  }
}));

export const ResourceMatchItemMatch = ({
  matchClasses: matchClassesOverride,
  resourceRequest,
  buttonType,
  setAnchorEl,
  setPopoverResourceRequest,
  project
}) => {
  const me = useMeContext();
  const classes = useStyles();
  const matchClasses = useMatchStyles({ classes: matchClassesOverride });
  const userWithRolesClasses = useUserWithRolesStyles();
  const { formatMessage } = useIntl();
  const { resourceMatches } = resourceRequest;
  const [resourceMatch] = resourceMatches;

  const handlePopover = useCallback(
    event => {
      setAnchorEl(event.currentTarget);
      setPopoverResourceRequest(resourceRequest);
    },
    [setAnchorEl, setPopoverResourceRequest, resourceRequest]
  );

  const { loading, resourceUsers } = useGetResourceUser({
    page: 1,
    pageSize: 1,
    userUris: [resourceMatch.userUri],
    date: getTodayForUser(me),
    includeRoles: false
  });

  const user = useMemo(
    () => (!loading && resourceUsers.length ? resourceUsers[0] : null),
    [loading, resourceUsers]
  );

  const hasPermission =
    hasProposeActionAllowed(me) || hasAcceptActionAllowed(project);

  const dataQeId = 'ResourceMatchItemMatch';

  return user ? (
    <div className={classes.itemRoot} role="gridcell">
      <ButtonBase
        data-qe-id={dataQeId}
        className={matchClasses.matchButtonContainer}
        onClick={handlePopover}
      >
        <div className={matchClasses.matchInfo}>
          <div className={classes.itemContainer}>
            <div className={classes.itemTextContainer}>
              <DelaStarIcon
                className={classNames(
                  classes.itemDelaStarIcon,
                  matchClasses.delaStarIcon
                )}
              />
              <Typography variant="caption" color="textSecondary">
                {formatMessage({
                  id: 'resourceMatchItem.suggestedResource'
                })}
              </Typography>
            </div>
            <div className={classes.itemUserRow}>
              <UserWithRoles
                qeIdTag={dataQeId}
                classes={userWithRolesClasses}
                user={user}
              />
              <BusyIcon
                requestLoad={resourceRequest.load}
                availabilityPercentage={
                  resourceMatch.availabilityPercentageInDateRange
                }
              />
            </div>
          </div>
          <ArrowDropDownSharpIcon className={classes.itemDropdownIcon} />
        </div>
      </ButtonBase>
      {hasPermission ? (
        buttonType === RESOURCE_MATCH_ITEM_BUTTONS.ACCEPT ? (
          <ResourceMatchItemAcceptButton
            resourceRequest={resourceRequest}
            dataQeId={dataQeId}
          />
        ) : (
          <ResourceMatchItemProposeButton
            resourceRequest={resourceRequest}
            dataQeId={dataQeId}
          />
        )
      ) : null}
    </div>
  ) : (
    <ResourceMatchItemSkeleton dataQeId={dataQeId} />
  );
};

ResourceMatchItemMatch.propTypes = {
  matchClasses: PropTypes.object,
  resourceRequest: PropTypes.object.isRequired,
  buttonType: PropTypes.oneOf(Object.values(RESOURCE_MATCH_ITEM_BUTTONS))
    .isRequired,
  setAnchorEl: PropTypes.func.isRequired,
  setPopoverResourceRequest: PropTypes.func.isRequired,
  project: PropTypes.object.isRequired
};

export default ResourceMatchItemMatch;
